import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import { useClient } from '../../../context/client';
import { submitPaymentToBC } from '../../../middleware/client';
import { alertsManager } from '../../../utils';
import { useCurrency, useCustomerGroup } from '../context';

function getBillingAddressValue(customer, name) {
	// TODO: Make sure this is never undefined to simplify logic
	const billingSameAsShipping =
		customer.billing_address_same_as_shipping === undefined
			? true
			: customer.billing_address_same_as_shipping;

	if (!billingSameAsShipping) {
		// get value from billing address
		if (customer.billing_address && customer.billing_address[name])
			return customer.billing_address[name];

		return false;
	}

	// get value from shipping address
	if (customer.shipping_address && customer.shipping_address[name])
		return customer.shipping_address[name];

	return false;
}

function getModifierByName(name, product) {
	if (!product.modifiers || product.modifiers.length === 0) return false;

	for (const modifier of product.modifiers) {
		if (name.toLowerCase() === modifier.display_name.toLowerCase())
			return modifier;
	}
	return false;
}

function getModifiers(product, parentId) {
	const modifiers = [];
	// Add modifiers if they exist
	if (product.part_number && getModifierByName('Part Number', product)) {
		modifiers.push({
			id: getModifierByName('Part Number', product).id,
			value: product.part_number,
		});
	}
	if (product.row && getModifierByName('Row', product)) {
		modifiers.push({
			id: getModifierByName('Row', product).id,
			value: product.row,
		});
	}
	if (product.description && getModifierByName('Description', product)) {
		modifiers.push({
			id: getModifierByName('Description', product).id,
			value: product.description,
		});
	}
	if (product.diagram && getModifierByName('Diagram', product)) {
		modifiers.push({
			id: getModifierByName('Diagram', product).id,
			value: product.diagram,
		});
	}
	if (getModifierByName('Vehicle', product)) {
		modifiers.push({
			id: getModifierByName('Vehicle', product).id,
			value: [
				product.year || '',
				product.make || '',
				product.model || '',
				product.sub_model || '',
			].toString(),
		});
	}

	// If there is an Options modifier on the product, add any questions and addons to it
	const optionsModifier = getModifierByName('Options', product);
	if (optionsModifier) {
		// Create a JSON string from the selected options
		const optionsString = JSON.stringify({
			questions: Object.fromEntries(
				// Filter out option_values that are questions
				Object.entries(product.option_values).filter(
					([optionValueQuestion]) =>
						product.questions.some(
							({ question }) => question === optionValueQuestion,
						),
				),
			),
			addOnsParentId: parentId,
		});

		modifiers.push({ id: optionsModifier.id, value: optionsString });
	}

	return modifiers;
}

function getOptionIdByName(option, name) {
	for (const item of option.option_values)
		if (item.label.toLowerCase() === name.toLowerCase()) return item.id;

	return false;
}

function getProductColorOptionValue(option, product) {
	const optionValueId = getOptionIdByName(option, product.color);

	return {
		id: option.id,
		value: product.color ? optionValueId : '',
	};
}

function getProductPriceCodeOption(option, product) {
	const optionValueId = getOptionIdByName(
		option,
		product?.diagram.charAt(0) || '',
	);
	return {
		id: option.id,
		value: product?.diagram ? optionValueId : '',
	};
}

function getGenericProductOptionValues(option, product) {
	const name = option.display_name;
	const optionValueId = getOptionIdByName(
		option,
		product.option_values[name],
	);
	const values = { id: option.id };
	values.value = product.option_values[name] ? optionValueId : '';
	return {
		id: option.id,
		value: product.option_values[name] ? optionValueId : '',
	};
}

const productOptionNamesToIgnore = [
	'vehicle',
	'row',
	'description',
	'diagram',
	'options',
];

function getProductOptions(product, parentId, addOns) {
	if (product.is_custom_product) return [];

	return [
		...product.productOptions.reduce((optionArray, option) => {
			const name = option?.display_name.toLowerCase();
			if (productOptionNamesToIgnore.includes(name)) return optionArray;
			if (name === 'color') {
				return [
					...optionArray,
					getProductColorOptionValue(option, product),
				];
			}
			if (name === 'price code') {
				return [
					...optionArray,
					getProductPriceCodeOption(option, product),
				];
			}
			return [
				...optionArray,
				getGenericProductOptionValues(option, product),
			];
		}, []),
		...getModifiers(product, parentId, addOns),
	];
}

function createMessage(title, contents, type, timeout) {
	const alert = {
		header: title,
		messages: [{ text: contents }],
		type: type,
		onClose: () => null,
	};

	const alertKey = alertsManager.add(alert);

	if (timeout) setTimeout(() => alertsManager.remove(alertKey), timeout);
}

async function submitPayment(id, payment) {
	const payload = {
		payment: {
			instrument: {
				type: 'card',
				cardholder_name: payment.name_on_card,
				number: payment.card_number,
				expiry_month: parseInt(payment.expiration_month),
				expiry_year: parseInt(payment.expiration_year),
				verification_value: payment.cvv,
			},
			payment_method_id: 'authorizenet.card',
		},
	};
	return await submitPaymentToBC(payload, id);
}

const requiredFields = {
	customer: ['email', 'first_name', 'last_name'],
	payment: ['payment_type'],
};

function checkRequiredFields(customer, payment, productsInOrder) {
	const failedValidation = [];
	// check all the customer required fields
	for (const item of requiredFields.customer) {
		if (!customer[item])
			failedValidation.push(`customer ${item} must contain a value`);
	}
	// check all the payment required fields
	for (const item of requiredFields.payment) {
		if (!payment[item])
			failedValidation.push(`payment ${item} must contain a value`);
	}
	// Make sure the order contains a product
	if (productsInOrder.length === 0)
		failedValidation.push(`Order must contain at least one item`);

	// Make sure all the modifiers are selected.
	for (const product of productsInOrder) {
		for (const { question } of product.questions || []) {
			if (!product.option_values || !product.option_values[question])
				failedValidation.push(`must set product question ${question}`);
		}
	}

	if (failedValidation.length > 0) {
		const contents = failedValidation.map((item, index) => (
			<div key={index}>{item}</div>
		));
		createMessage(
			'The following errors need to be corrected',
			contents,
			'error',
			15000,
		);
	}

	return failedValidation.length <= 0;
}

const addressValues = [
	{ customerValue: 'first_name', dataValue: 'first_name' },
	{ customerValue: 'last_name', dataValue: 'last_name' },
	{ customerValue: 'company', dataValue: 'company' },
	{ customerValue: 'address1', dataValue: 'street_1' },
	{ customerValue: 'address2', dataValue: 'street_2' },
	{ customerValue: 'city', dataValue: 'city' },
	{ customerValue: 'state_or_province', dataValue: 'state' },
	{ customerValue: 'postal_code', dataValue: 'zip' },
	{ customerValue: 'country', dataValue: 'country' },
	{ customerValue: 'email', dataValue: 'email' },
];

function createCustomerData(customer, customerGroup) {
	// Create customer object
	const customerSection = {
		email: customer.email,
		first_name: customer.first_name,
		last_name: customer.last_name,
		phone: customer.phone,
	};

	// add optional customer data
	if (customer.company) customerSection.company = customer.company;

	if (customer.order_comments)
		customerSection.notes = customer.order_comments;

	if (customerGroup) customerSection.customer_group_id = customerGroup;

	return customerSection;
}

function reduceProductsInOrderToLineItems(lineItems, product) {
	const parentId = uuidv4();

	const addOnLineItems = Object.values(product.selectedAddOns || {})
		.filter(({ added }) => !!added)
		.map((addOn) => {
			const product_options = [];

			// We will store this unique identifier in the options value so that we can link it to the parent product later
			const optionsModifier = getModifierByName('Options', addOn);

			if (optionsModifier) {
				product_options.push({
					id: optionsModifier.id,
					value: JSON.stringify({ parentId }),
				});
			}

			return {
				product_id: addOn.id,
				quantity: addOn.quantity || 1,
				price_inc_tax:
					addOn.new_price ?? (addOn.sale_price || addOn.price),
				price_ex_tax:
					addOn.new_price ?? (addOn.sale_price || addOn.price),
				product_options,
			};
		});

	if (product.is_custom_product) {
		// Store custom product fields in row_comment as JSON for later extraction
		const customFieldsData = {
			vehicle: `${product.year || ''},${product.make || ''},${product.model || ''},${product.sub_model || ''}`,
			color: product.color || '',
			part_number: product.part_number || '',
			seat: product.seat || '',
			row: product.row || '',
		};
		
		// Store custom data in row_comment with a special prefix
		const row_comment = `CUSTOM_FIELDS:${JSON.stringify(customFieldsData)}`;

		return [
			...lineItems,
			{
				name: product.name,
				quantity: product.quantity || 1,
				price_inc_tax: product.priceIncTax,
				price_ex_tax: product.priceExcTax,
				name_merchant: product.vendor,
				row_comment: row_comment,
			},
			...addOnLineItems,
		];
	} else {
		return [
			...lineItems,
			{
				product_id: product.product_id,
				quantity: product.quantity || 1,
				price_inc_tax:
					product.new_price ?? (product.sale_price || product.price),
				price_ex_tax:
					product.new_price ?? (product.sale_price || product.price),
				product_options: getProductOptions(
					product,
					parentId,
					addOnLineItems.map(({ product_id }) => product_id),
				),
				row_comment: product.row_comment || '',
			},
			...addOnLineItems,
		];
	}
}

function createOrderData(customer, payment, productsInOrder, currency) {
	// Create Order object
	const orderSection = {
		products: productsInOrder.reduce(reduceProductsInOrderToLineItems, []),
		shipping_addresses: [{}],
		billing_address: {},
		default_currency_code: currency,
		payment_method: payment.payment_type,
		status_id: 0,
	};

	// Set Shipping Address data (because it is not required)
	if (customer.shipping_address) {
		for (const item of addressValues) {
			if (customer.shipping_address[item.customerValue]) {
				orderSection.shipping_addresses[0][item.dataValue] =
					customer.shipping_address[item.customerValue];
			}
		}
	}
	// set values from the customer section
	orderSection.shipping_addresses[0].first_name = customer.first_name;
	orderSection.shipping_addresses[0].last_name = customer.last_name;
	orderSection.shipping_addresses[0].phone = customer.phone || ' ';
	orderSection.shipping_addresses[0].company = customer.company || '';

	// Set Billing Address data
	for (const item of addressValues) {
		if (item.customerValue !== 'company') {
			const billingAddress = getBillingAddressValue(
				customer,
				item.customerValue,
			);

			if (billingAddress)
				orderSection.billing_address[item.dataValue] = billingAddress;
		}
	}
	orderSection.billing_address.first_name = customer.first_name;
	orderSection.billing_address.last_name = customer.last_name;
	orderSection.billing_address.email = customer.email;
	orderSection.billing_address.phone = customer.phone || ' ';
	orderSection.billing_address.company = customer.company || '';

	// Other order data
	// The BigCommerce API requires shipping cost to be handled very specifically
	// Only add shipping cost if it's a valid positive number
	if (payment.shipping_charge) {
		const shippingCost = parseFloat(payment.shipping_charge);
		console.log('shipping_charge value:', payment.shipping_charge);
		console.log('parsed shipping cost:', shippingCost);
		
		// Only set base_shipping_cost if it's a valid positive number
		if (!isNaN(shippingCost) && shippingCost > 0) {
			// Format with exactly 2 decimal places and ensure it's a number
			orderSection.base_shipping_cost = Number(shippingCost.toFixed(2));
		}
	}

	if (customer.order_comments)
		orderSection.staff_notes = customer.order_comments;

	if (payment.payment_type === 'Amazon')
		orderSection.external_id = payment.order_id;

	return orderSection;
}

function checkOrderItensIntegrity(payload) {
	if (!payload.order.products) return true;

	let products = payload.order.products;
	for (let product of products) {
		if (!checkProductOptionsIntegrity(product, payload.order.products))
			return false;
	}
	return true;
}

function checkProductOptionsIntegrity(product, products) {
	let hasIntegrity = false,
		incompatibleHashes = [],
		parentid = getOptionsKeyValue(product, 'parentId');

	if (!parentid) return true;
	if (!product.id && product.name_merchant) return true;

	for (const currentProduct of products) {
		if (currentProduct.product_id === product.product_id) continue;

		let addOnsParentId = getOptionsKeyValue(
			currentProduct,
			'addOnsParentId',
		);

		if (!addOnsParentId) continue;

		if (parentid === addOnsParentId) return true;

		incompatibleHashes.push(currentProduct.product_id);
	}

	createMessage(
		'There is an issue with the Order Line Items',
		`The Addon product ${
			product.product_id
		} is not matching with ${incompatibleHashes.toString()}`,
		'error',
		15000,
	);

	return hasIntegrity;
}

function getOptionsKeyValue(product, key) {
	if (product.product_options === undefined) return true;

	for (const option of product.product_options) {
		if (typeof option.value != 'string') continue;

		if (option.value.includes(key)) {
			let jsonValue = JSON.parse(option.value);
			return jsonValue.key;
		}
	}

	return false;
}

/**
 * @callback SubmitOrder
 * @param {Object} customer
 * @param {Object} payment
 * @param {Array} productsInOrder
 */

/**
 * @return {[boolean, SubmitOrder]}
 */
export default function useSubmitOrder() {
	/** @type {[boolean, Function]} */
	const [isPlacingOrder, setIsPlacingOrder] = useState(false);
	const [currency] = useCurrency();
	const navigate = useNavigate();
	const client = useClient();
	const [customerGroup] = useCustomerGroup();

	/** @type {SubmitOrder} */
	const submitOrder = useCallback(
		async (customer, payment, productsInOrder) => {
			setIsPlacingOrder(true);

			if (!checkRequiredFields(customer, payment, productsInOrder)) {
				setIsPlacingOrder(false);
				return;
			}

			const bodyData = {
				customer: createCustomerData(customer, customerGroup),
				order: createOrderData(
					customer,
					payment,
					productsInOrder,
					currency,
				),
				payment_type: payment.payment_type
					.toLowerCase()
					.replace(' ', '_'),
				images: customer.photos,
				po_number: payment.po_number || '',
			};

			if (!checkOrderItensIntegrity(bodyData)) {
				setIsPlacingOrder(false);
				return;
			}

			if (payment.payment_type === 'Amazon')
				bodyData.ship_by = payment.ship_by;
				
			// Debug the payload to check for issues
			console.log('Order payload:', JSON.stringify(bodyData, null, 2));

			try {
				const response = await client.createOrder(bodyData);

				if (response.id) {
					const { errors, data } = await submitPayment(
						response.id,
						payment,
					);
					if (errors) {
						// show errors.
						const content = [];
						for (const error in errors)
							content.push(<div>{errors[error]}</div>);

						createMessage(
							'There was an issue with the payment',
							content,
							'error',
							15000,
						);
					} else if (data && data.status === 'success') {
						// handle success
						createMessage(
							'Successfully created the order',
							'',
							'success',
							10000,
						);
						navigate(`/orders`);
					}
				} else if (response.status === 204) {
					createMessage(
						'Successfully created the order',
						'',
						'success',
						10000,
					);
					navigate(`/orders`);
				}
			} catch (error) {
				console.error(error);
				createMessage(
					'Failed to create order. Please try again',
					'',
					'error',
					10000,
				);
			} finally {
				setIsPlacingOrder(false);
			}
		},
		[client, navigate, customerGroup, currency],
	);

	return [isPlacingOrder, submitOrder];
}
